import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import {
    Box, Paper, Typography, TextField, MenuItem, CircularProgress, Button, Chip, Grid,
    Tabs, Tab, Checkbox, FormControlLabel, FormGroup, Divider, LinearProgress,
    IconButton, Tooltip
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSnackbar } from 'notistack';
import { getDisciplineOffers } from '../../../store/actions/disciplineOfferActions';
import {
    fetchDofContents, getAtaPresencaData, markAtaGerada,
    fetchBatchDofContents, getAtaPresencaBatchData, markBatchAtaGerada,
    toggleOkSecretaria
} from '../../../store/actions/academicoActions';
import { RootState } from '../../../store/reducers';
import { FunctionalAtaPresencaComponentToPrint, FunctionalAtaPresencaBatchToPrint } from './AtaPresencaImpressao';
import { isProfessorRole, canToggleOkSecretaria } from '../../../utils/permissions';

const AtaPresenca = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const roleName = useSelector((state: RootState) => state.auth.roleName);
    const teacherId = useSelector((state: RootState) => state.auth.teacherId);
    const isProfessor = isProfessorRole(roleName);
    const canToggleOk = canToggleOkSecretaria(roleName);

    const disciplineOffers = useSelector((state: RootState) => state.disciplineOffers.disciplineOffers);
    const dofContents = useSelector((state: RootState) => state.academico.dofContents);
    const ataPresenca = useSelector((state: RootState) => state.academico.ataPresenca);
    const isFetchingAta = useSelector((state: RootState) => state.academico.isFetchingAta);
    const isFetchingDofContents = useSelector((state: RootState) => state.academico.isFetchingDofContents);
    const batchDofContents = useSelector((state: RootState) => state.academico.batchDofContents);
    const ataPresencaBatch = useSelector((state: RootState) => state.academico.ataPresencaBatch);
    const isFetchingBatch = useSelector((state: RootState) => state.academico.isFetchingBatch);
    const isFetchingBatchContents = useSelector((state: RootState) => state.academico.isFetchingBatchContents);

    // Tab
    const [tabMode, setTabMode] = useState(0);

    // Filters
    const [filterPeriodo, setFilterPeriodo] = useState<string>('');
    const [filterProfessor, setFilterProfessor] = useState<string>(isProfessor && teacherId ? String(teacherId) : '');
    const [filterDataInicio, setFilterDataInicio] = useState<string>('');
    const [filterDataFim, setFilterDataFim] = useState<string>('');

    // Individual selectors
    const [selectedDofId, setSelectedDofId] = useState<number | ''>('');
    const [selectedContentId, setSelectedContentId] = useState<number | ''>('');
    const [readyToPrint, setReadyToPrint] = useState(false);

    // Batch selectors
    const [selectedBatchItems, setSelectedBatchItems] = useState<Set<string>>(new Set());
    const [readyToPrintBatch, setReadyToPrintBatch] = useState(false);

    const componentRef = React.useRef(null);
    const batchComponentRef = React.useRef(null);

    useEffect(() => {
        dispatch(getDisciplineOffers(isProfessor ? teacherId : null));
    }, [dispatch, isProfessor, teacherId]);

    // Extract unique periods and professors from DOFs
    const periodos = useMemo(() => {
        const map = new Map<string, string>();
        (disciplineOffers || []).forEach((dof: any) => {
            const desc = dof?.period?.Description;
            if (desc) map.set(desc, desc);
        });
        return Array.from(map.values()).sort();
    }, [disciplineOffers]);

    const professores = useMemo(() => {
        const map = new Map<number, { id: number; name: string }>();
        (disciplineOffers || []).forEach((dof: any) => {
            const t = dof?.teacher;
            if (t && t.id) {
                const name = `${t.FirstName || ''} ${t.LastName || ''}`.trim();
                if (!map.has(t.id)) map.set(t.id, { id: t.id, name });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [disciplineOffers]);

    // Filtered DOFs based on period and professor
    const filteredDofs = useMemo(() => {
        let list = disciplineOffers || [];
        if (filterPeriodo) {
            list = list.filter((dof: any) => dof?.period?.Description === filterPeriodo);
        }
        if (filterProfessor) {
            list = list.filter((dof: any) => String(dof?.teacher?.id) === filterProfessor);
        }
        return list;
    }, [disciplineOffers, filterPeriodo, filterProfessor]);

    // Filtered contents for individual mode
    const filteredContents = useMemo(() => {
        let list = dofContents || [];
        if (filterDataInicio) {
            list = list.filter((c: any) => {
                if (!c.Date) return false;
                return c.Date.substring(0, 10) >= filterDataInicio;
            });
        }
        if (filterDataFim) {
            list = list.filter((c: any) => {
                if (!c.Date) return false;
                return c.Date.substring(0, 10) <= filterDataFim;
            });
        }
        return list;
    }, [dofContents, filterDataInicio, filterDataFim]);

    // Build batch items list: DOF + contents grouped, filtered by date
    const batchItemsList = useMemo(() => {
        const result: { dof: any; contents: any[] }[] = [];
        filteredDofs.forEach((dof: any) => {
            const contents = batchDofContents[dof.id] || [];
            let filtered = contents;
            if (filterDataInicio) {
                filtered = filtered.filter((c: any) => c.Date && c.Date.substring(0, 10) >= filterDataInicio);
            }
            if (filterDataFim) {
                filtered = filtered.filter((c: any) => c.Date && c.Date.substring(0, 10) <= filterDataFim);
            }
            // Show all contents (generated and pending)
            if (filtered.length > 0) {
                result.push({ dof, contents: filtered });
            }
        });
        return result;
    }, [filteredDofs, batchDofContents, filterDataInicio, filterDataFim]);

    // Total selectable items count
    const totalBatchItems = useMemo(() => {
        return batchItemsList.reduce((sum, g) => sum + g.contents.length, 0);
    }, [batchItemsList]);

    // Reset DOF selection when filters change
    useEffect(() => {
        setSelectedDofId('');
        setSelectedContentId('');
    }, [filterPeriodo, filterProfessor]);

    // Fetch contents when DOF changes (individual mode)
    useEffect(() => {
        if (selectedDofId) {
            setSelectedContentId('');
            dispatch(fetchDofContents(selectedDofId as number));
        }
    }, [dispatch, selectedDofId]);

    // Reset content selection when date filters change
    useEffect(() => {
        setSelectedContentId('');
    }, [filterDataInicio, filterDataFim]);

    // Fetch batch contents when switching to Lote tab or when filters change
    useEffect(() => {
        if (tabMode === 1 && filteredDofs.length > 0) {
            const dofIds = filteredDofs.map((d: any) => d.id);
            dispatch(fetchBatchDofContents(dofIds));
            setSelectedBatchItems(new Set());
        }
    }, [dispatch, tabMode, filteredDofs]);

    // When ata data arrives and we're ready to print (individual), trigger print
    useEffect(() => {
        if (readyToPrint && ataPresenca && !isFetchingAta) {
            setTimeout(() => {
                handlePrint();
            }, 300);
        }
    }, [readyToPrint, ataPresenca, isFetchingAta]);

    // When batch ata data arrives and we're ready to print, trigger batch print
    useEffect(() => {
        if (readyToPrintBatch && ataPresencaBatch.length > 0 && !isFetchingBatch) {
            setTimeout(() => {
                handlePrintBatch();
            }, 300);
        }
    }, [readyToPrintBatch, ataPresencaBatch, isFetchingBatch]);

    // --- Individual print ---
    const handleAfterPrint = useCallback(() => {
        if (selectedContentId) {
            dispatch(markAtaGerada(selectedContentId as number));
            if (selectedDofId) {
                dispatch(fetchDofContents(selectedDofId as number));
            }
            enqueueSnackbar('Ata gerada com sucesso!', { variant: 'success' });
        }
        setReadyToPrint(false);
    }, [dispatch, selectedContentId, selectedDofId, enqueueSnackbar]);

    const reactToPrintContent = useCallback(() => {
        return componentRef.current;
    }, [componentRef.current]);

    const handlePrint = useReactToPrint({
        content: reactToPrintContent,
        documentTitle: "Ata de Presenca",
        onAfterPrint: handleAfterPrint,
        removeAfterPrint: true,
    });

    const handleGerarAta = () => {
        if (!selectedDofId || !selectedContentId) return;
        setReadyToPrint(true);
        dispatch(getAtaPresencaData(selectedDofId as number, selectedContentId as number));
    };

    // --- Batch print ---
    const handleAfterPrintBatch = useCallback(() => {
        const contentIds = Array.from(selectedBatchItems).map(key => Number(key.split(':')[1]));
        dispatch(markBatchAtaGerada(contentIds));
        // Refresh batch contents
        const dofIds = filteredDofs.map((d: any) => d.id);
        dispatch(fetchBatchDofContents(dofIds));
        setSelectedBatchItems(new Set());
        setReadyToPrintBatch(false);
        enqueueSnackbar(`${contentIds.length} ata(s) gerada(s) com sucesso!`, { variant: 'success' });
    }, [dispatch, selectedBatchItems, filteredDofs, enqueueSnackbar]);

    const reactToPrintBatchContent = useCallback(() => {
        return batchComponentRef.current;
    }, [batchComponentRef.current]);

    const handlePrintBatch = useReactToPrint({
        content: reactToPrintBatchContent,
        documentTitle: "Atas de Presenca - Lote",
        onAfterPrint: handleAfterPrintBatch,
        removeAfterPrint: true,
    });

    const handleGerarAtasLote = () => {
        if (selectedBatchItems.size === 0) return;
        const items = Array.from(selectedBatchItems).map(key => {
            const [dofId, contentId] = key.split(':').map(Number);
            return { dofId, contentId };
        });
        setReadyToPrintBatch(true);
        dispatch(getAtaPresencaBatchData(items));
    };

    // --- Batch selection helpers ---
    const makeBatchKey = (dofId: number, contentId: number) => `${dofId}:${contentId}`;

    const toggleBatchItem = (dofId: number, contentId: number) => {
        const key = makeBatchKey(dofId, contentId);
        setSelectedBatchItems(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedBatchItems.size === totalBatchItems) {
            setSelectedBatchItems(new Set());
        } else {
            const all = new Set<string>();
            batchItemsList.forEach(g => {
                g.contents.forEach((c: any) => {
                    all.add(makeBatchKey(g.dof.id, c.id));
                });
            });
            setSelectedBatchItems(all);
        }
    };

    const toggleDofGroup = (dof: any, contents: any[]) => {
        const keys = contents.map((c: any) => makeBatchKey(dof.id, c.id));
        const allSelected = keys.every(k => selectedBatchItems.has(k));
        setSelectedBatchItems(prev => {
            const next = new Set(prev);
            keys.forEach(k => {
                if (allSelected) next.delete(k);
                else next.add(k);
            });
            return next;
        });
    };

    // --- Helpers ---
    const getDofLabel = (dof: any) => {
        const id = dof?.id || '';
        const discipline = dof?.disciplineModule?.discipline?.Description || '';
        const module = dof?.disciplineModule?.Description || '';
        const classOffer = dof?.classOffer?.Description || '';
        const dtIni = formatDate(dof?.InitialDate);
        const dtFim = formatDate(dof?.EndDate);
        const periodo = dtIni || dtFim ? ` | ${dtIni} - ${dtFim}` : '';
        return `[${id}] ${discipline} - ${module} (${classOffer})${periodo}`;
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const getContentLabel = (c: any) => {
        const id = c.id || '';
        const dt = formatDate(c.Date);
        const hrs = c.QtyHours ? `${c.QtyHours}h` : '';
        const desc = c.Content ? ` - ${c.Content}` : '';
        return `[${id}] ${dt} ${hrs}${desc}`;
    };

    const contentHelperText = () => {
        if (!selectedDofId) return '';
        if (isFetchingDofContents) return 'Carregando conteudos...';
        if (dofContents.length === 0) return 'Nenhum conteudo encontrado para esta DOF';
        if (filteredContents.length === 0 && (filterDataInicio || filterDataFim))
            return `Nenhum conteudo no periodo selecionado (${dofContents.length} total)`;
        return `${filteredContents.length} conteudo(s)`;
    };

    const handleToggleOkSecretaria = async (contentId: number, currentValue: boolean) => {
        try {
            await dispatch(toggleOkSecretaria(contentId, currentValue));
            enqueueSnackbar(
                currentValue ? 'OK Secretaria removido' : 'OK Secretaria aprovado',
                { variant: 'success' }
            );
        } catch {
            enqueueSnackbar('Erro ao atualizar OK Secretaria', { variant: 'error' });
        }
    };

    const renderOkSecretariaChip = (content: any) => {
        if (canToggleOk) {
            return (
                <Tooltip title={content.okSecretaria ? 'Remover OK Secretaria' : 'Aprovar OK Secretaria'}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleToggleOkSecretaria(content.id, !!content.okSecretaria);
                        }}
                        sx={{ ml: 0.5 }}
                    >
                        {content.okSecretaria
                            ? <CheckCircleIcon fontSize="small" color="info" />
                            : <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'grey.400' }} />
                        }
                    </IconButton>
                </Tooltip>
            );
        }
        if (content.okSecretaria) {
            return <Chip label="OK Secretaria" size="small" color="info" sx={{ ml: 1 }} />;
        }
        return null;
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Ata de Presenca</Typography>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Filtros</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Periodo"
                            select
                            value={filterPeriodo}
                            onChange={(e) => setFilterPeriodo(e.target.value)}
                            fullWidth
                            size="small"
                        >
                            <MenuItem value="">
                                <em>Todos</em>
                            </MenuItem>
                            {periodos.map((p: string) => (
                                <MenuItem key={p} value={p}>{p}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Professor"
                            select
                            value={filterProfessor}
                            onChange={(e) => setFilterProfessor(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={isProfessor}
                            helperText={isProfessor ? 'Filtrado pelo seu usuario' : ''}
                        >
                            <MenuItem value="">
                                <em>Todos</em>
                            </MenuItem>
                            {professores.map((p) => (
                                <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data Aula (Inicio)"
                            type="date"
                            value={filterDataInicio}
                            onChange={(e) => setFilterDataInicio(e.target.value)}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data Aula (Fim)"
                            type="date"
                            value={filterDataFim}
                            onChange={(e) => setFilterDataFim(e.target.value)}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Tabs */}
            <Paper sx={{ mb: 2 }}>
                <Tabs value={tabMode} onChange={(_, v) => setTabMode(v)}>
                    <Tab label="Individual" />
                    <Tab label="Impressao em Lote" />
                </Tabs>
            </Paper>

            {/* Individual Mode */}
            {tabMode === 0 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <TextField
                        label="Disciplina Ofertada"
                        select
                        value={selectedDofId}
                        onChange={(e) => setSelectedDofId(Number(e.target.value))}
                        fullWidth
                        sx={{ mb: 2 }}
                        helperText={selectedDofId ? `DOF ID: ${selectedDofId}` : ''}
                    >
                        <MenuItem value="">
                            <em>Selecione uma oferta...</em>
                        </MenuItem>
                        {filteredDofs.map((dof: any) => (
                            <MenuItem key={dof.id} value={dof.id}>
                                {getDofLabel(dof)}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Conteudo / Aula"
                        select
                        value={selectedContentId}
                        onChange={(e) => setSelectedContentId(Number(e.target.value))}
                        fullWidth
                        disabled={!selectedDofId || isFetchingDofContents}
                        sx={{ mb: 2 }}
                        helperText={contentHelperText()}
                    >
                        <MenuItem value="">
                            <em>{isFetchingDofContents ? 'Carregando...' : 'Selecione um conteudo...'}</em>
                        </MenuItem>
                        {filteredContents.map((c: any) => (
                            <MenuItem key={c.id} value={c.id}>
                                {getContentLabel(c)}
                                {c.isAtaGerada && (
                                    <Chip
                                        label="Ata Gerada"
                                        size="small"
                                        color="success"
                                        sx={{ ml: 1 }}
                                    />
                                )}
                                {renderOkSecretariaChip(c)}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Button
                        variant="contained"
                        startIcon={isFetchingAta ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                        onClick={handleGerarAta}
                        disabled={!selectedDofId || !selectedContentId || isFetchingAta}
                    >
                        {isFetchingAta ? 'Gerando...' : 'Gerar Ata'}
                    </Button>
                </Paper>
            )}

            {/* Batch Mode */}
            {tabMode === 1 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    {isFetchingBatchContents && <LinearProgress sx={{ mb: 2 }} />}

                    {!isFetchingBatchContents && batchItemsList.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            Nenhum conteudo pendente encontrado para os filtros selecionados.
                        </Typography>
                    )}

                    {batchItemsList.length > 0 && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedBatchItems.size === totalBatchItems && totalBatchItems > 0}
                                            indeterminate={selectedBatchItems.size > 0 && selectedBatchItems.size < totalBatchItems}
                                            onChange={toggleSelectAll}
                                        />
                                    }
                                    label={<Typography variant="subtitle2">Selecionar Todos ({totalBatchItems} conteudos)</Typography>}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {selectedBatchItems.size} selecionado(s)
                                </Typography>
                            </Box>

                            <Divider sx={{ mb: 1 }} />

                            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                                {batchItemsList.map(({ dof, contents }) => (
                                    <Box key={dof.id} sx={{ mb: 1 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={contents.every((c: any) => selectedBatchItems.has(makeBatchKey(dof.id, c.id)))}
                                                    indeterminate={
                                                        contents.some((c: any) => selectedBatchItems.has(makeBatchKey(dof.id, c.id)))
                                                        && !contents.every((c: any) => selectedBatchItems.has(makeBatchKey(dof.id, c.id)))
                                                    }
                                                    onChange={() => toggleDofGroup(dof, contents)}
                                                />
                                            }
                                            label={
                                                <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>
                                                    {getDofLabel(dof)}
                                                </Typography>
                                            }
                                        />
                                        <FormGroup sx={{ pl: 4 }}>
                                            {contents.map((c: any) => (
                                                <FormControlLabel
                                                    key={c.id}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedBatchItems.has(makeBatchKey(dof.id, c.id))}
                                                            onChange={() => toggleBatchItem(dof.id, c.id)}
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2">
                                                                {getContentLabel(c)}
                                                            </Typography>
                                                            {c.isAtaGerada && (
                                                                <Chip label="Ata Gerada" size="small" color="success" />
                                                            )}
                                                            {renderOkSecretariaChip(c)}
                                                        </Box>
                                                    }
                                                />
                                            ))}
                                        </FormGroup>
                                    </Box>
                                ))}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Button
                                variant="contained"
                                startIcon={isFetchingBatch ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                                onClick={handleGerarAtasLote}
                                disabled={selectedBatchItems.size === 0 || isFetchingBatch}
                            >
                                {isFetchingBatch ? `Gerando ${selectedBatchItems.size} ata(s)...` : `Gerar ${selectedBatchItems.size} Ata(s) em Lote`}
                            </Button>
                        </>
                    )}
                </Paper>
            )}

            {/* Hidden print components */}
            <div style={{ display: 'none' }}>
                <FunctionalAtaPresencaComponentToPrint
                    ref={componentRef}
                    ataData={ataPresenca}
                />
                <FunctionalAtaPresencaBatchToPrint
                    ref={batchComponentRef}
                    atas={ataPresencaBatch}
                />
            </div>
        </Box>
    );
};

export default AtaPresenca;
